/*
 * Brand story carousel.
 *
 * Deliberately not Dawn's <slideshow-component>. That class needs a
 * button[name="next"] or SliderComponent's constructor returns before it
 * initialises anything, and its autoplay path dereferences a `reducedMotion`
 * property it only assigns for an .announcement-bar-slider. This design has
 * neither arrows nor a play/pause control, so satisfying Dawn would mean
 * shipping hidden UI that exists purely to pass a guard clause.
 *
 * Scroll snapping and scrollbar hiding still come from Dawn's
 * component-slider.css — that part is pure CSS and works as-is.
 */
class BrandStoryCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.bs__slider');
    this.dots = Array.from(this.querySelectorAll('.bs__dot'));
    if (!this.track || this.dots.length < 2) return;

    this.slides = Array.from(this.track.children);
    if (this.slides.length !== this.dots.length) return;

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goTo(index));
    });

    this.track.addEventListener('scroll', () => this.scheduleSync(), { passive: true });
    this.sync();
  }

  goTo(index) {
    const target = this.slides[index].offsetLeft - this.slides[0].offsetLeft;
    // scroll-behavior: smooth comes from .slider--everywhere; honour reduced motion.
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: target, behavior: reduce ? 'auto' : 'smooth' });
  }

  scheduleSync() {
    if (this.pending) return;
    this.pending = requestAnimationFrame(() => {
      this.pending = null;
      this.sync();
    });
  }

  /* Whichever slide is nearest the track's scroll position owns the active dot. */
  sync() {
    const origin = this.slides[0].offsetLeft;
    const position = this.track.scrollLeft;

    let active = 0;
    let closest = Infinity;
    this.slides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - origin - position);
      if (distance < closest) {
        closest = distance;
        active = index;
      }
    });

    this.dots.forEach((dot, index) => {
      const isActive = index === active;
      dot.classList.toggle('bs__dot--active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }
}

customElements.define('brand-story-carousel', BrandStoryCarousel);
